import type { AlgorithmChallenge } from '@code-arcade/engine'

export const heapSort: AlgorithmChallenge = {
  id: 'heap-sort',
  title: 'Heap Sort',
  level: 3,
  chapter: 'chapter-4',
  mode: 'race-it',
  description: 'Build a max-heap in O(n), then extract the maximum repeatedly in O(log n) — sorted in place, O(1) space.',

  realWorld: {
    scenario: "Omnitech's push notification service must deliver 2M pending notifications in priority order — paid users first, free users second, within each tier by urgency score. The queue changes mid-delivery as new urgent items arrive.",
    consequence: 'An array sorted once goes stale in seconds. A list re-sorted on every insert is O(n) per insert. A heap gives O(log n) insert and O(log n) pop — the queue stays ordered at all times without a full resort.',
    products: [],
  },

  visualization: {
    type: 'bar-array',
    defaultSize: 16,
    allowCustomInput: false,
  },

  interrupts: [
    {
      atStep: 25,
      character: 'grant-fielding',
      mood: 'idle',
      message: "The heap property: every parent is larger than its children. That invariant is the whole trick.",
    },
  ],

  complexity: { time: 'O(n log n)', space: 'O(1)' },
  xpReward: { oneStar: 200, twoStar: 400, threeStar: 800 },

  keyTakeaway:
    'A heap is the right tool whenever you need the minimum or maximum of a dynamically changing set. ' +
    'Job schedulers, Kubernetes pod eviction, Dijkstra\'s shortest path, database query cost estimators — ' +
    'they all maintain a heap. Heap sort teaches you the structure: if you ever find yourself sorting ' +
    'an array just to pop the smallest element, and then sorting again when something changes, you have ' +
    'the wrong data structure. The heap keeps the max (or min) invariant at O(log n) per operation, permanently.',

  whenToUse: [
    'Priority queues — task schedulers, Kubernetes pod eviction, notification delivery by urgency',
    'Dijkstra\'s and A* shortest-path — extract the minimum-cost node at each step',
    'Streaming top-K: maintain a min-heap of size K, evict when it overflows — O(n log K)',
    'When O(1) auxiliary space matters — merge sort needs O(n), heap sort needs O(1)',
    'OS process schedulers: always run the highest-priority runnable process',
  ],

  whenNotToUse: [
    'You need a stable sort — heap sort is not stable (equal elements may swap)',
    'Cache performance matters — heap\'s random-access pattern thrashes CPU caches; quicksort is faster in practice despite the same big-O',
    'Small arrays — insertion sort dominates on constant factors',
  ],

  pseudocode: `procedure heapSort(A)
  n ← length(A)

  // Phase 1: build max-heap in O(n)
  for i from ⌊n/2⌋ − 1 down to 0
    heapify(A, n, i)

  // Phase 2: extract max n−1 times
  for i from n − 1 down to 1
    swap A[0] and A[i]      // move max to sorted suffix
    heapify(A, i, 0)        // restore heap on [0..i−1]

procedure heapify(A, heapSize, root)
  largest ← root
  left ← 2·root + 1
  right ← 2·root + 2
  if left < heapSize and A[left] > A[largest]  then largest ← left
  if right < heapSize and A[right] > A[largest] then largest ← right
  if largest ≠ root
    swap A[root] and A[largest]
    heapify(A, heapSize, largest)`,

  codeSnippets: [
    {
      language: 'csharp',
      label: 'C#',
      code: `public static void HeapSort(int[] arr) {
    int n = arr.Length;
    for (int i = n / 2 - 1; i >= 0; i--) Heapify(arr, n, i);
    for (int i = n - 1; i > 0; i--) {
        (arr[0], arr[i]) = (arr[i], arr[0]);
        Heapify(arr, i, 0);
    }
}

static void Heapify(int[] arr, int n, int root) {
    int largest = root, l = 2 * root + 1, r = 2 * root + 2;
    if (l < n && arr[l] > arr[largest]) largest = l;
    if (r < n && arr[r] > arr[largest]) largest = r;
    if (largest != root) {
        (arr[root], arr[largest]) = (arr[largest], arr[root]);
        Heapify(arr, n, largest);
    }
}

// For a live priority queue, use the built-in:
// PriorityQueue<T, int> pq = new();
// pq.Enqueue(item, priority);  // O(log n)
// pq.Dequeue();                // O(log n)`,
    },
    {
      language: 'python',
      label: 'Python',
      code: `def heap_sort(arr: list[int]) -> list[int]:
    n = len(arr)
    for i in range(n // 2 - 1, -1, -1):
        _heapify(arr, n, i)
    for i in range(n - 1, 0, -1):
        arr[0], arr[i] = arr[i], arr[0]
        _heapify(arr, i, 0)
    return arr

def _heapify(arr, n, root):
    largest, l, r = root, 2 * root + 1, 2 * root + 2
    if l < n and arr[l] > arr[largest]: largest = l
    if r < n and arr[r] > arr[largest]: largest = r
    if largest != root:
        arr[root], arr[largest] = arr[largest], arr[root]
        _heapify(arr, n, largest)

# For a live priority queue (min-heap by default):
import heapq
heapq.heappush(pq, item)   # O(log n)
heapq.heappop(pq)           # O(log n)`,
    },
    {
      language: 'javascript',
      label: 'JavaScript',
      code: `function heapSort(arr) {
  const n = arr.length;
  for (let i = Math.floor(n / 2) - 1; i >= 0; i--) heapify(arr, n, i);
  for (let i = n - 1; i > 0; i--) {
    [arr[0], arr[i]] = [arr[i], arr[0]];
    heapify(arr, i, 0);
  }
  return arr;
}

function heapify(arr, n, root) {
  let largest = root, l = 2 * root + 1, r = 2 * root + 2;
  if (l < n && arr[l] > arr[largest]) largest = l;
  if (r < n && arr[r] > arr[largest]) largest = r;
  if (largest !== root) {
    [arr[root], arr[largest]] = [arr[largest], arr[root]];
    heapify(arr, n, largest);
  }
}

// No built-in heap in JS — implement one or use a library (heap.js, js-priority-queue)`,
    },
    {
      language: 'typescript',
      label: 'TypeScript',
      code: `function heapSort(arr: number[]): number[] {
  const n = arr.length;
  for (let i = Math.floor(n / 2) - 1; i >= 0; i--) heapify(arr, n, i);
  for (let i = n - 1; i > 0; i--) {
    [arr[0], arr[i]] = [arr[i], arr[0]];
    heapify(arr, i, 0);
  }
  return arr;
}

function heapify(arr: number[], n: number, root: number): void {
  let largest = root, l = 2 * root + 1, r = 2 * root + 2;
  if (l < n && arr[l] > arr[largest]) largest = l;
  if (r < n && arr[r] > arr[largest]) largest = r;
  if (largest !== root) {
    [arr[root], arr[largest]] = [arr[largest], arr[root]];
    heapify(arr, n, largest);
  }
}

// No built-in heap in TS/JS — implement one or use a library`,
    },
    {
      language: 'java',
      label: 'Java',
      code: `public static void heapSort(int[] arr) {
    int n = arr.length;
    for (int i = n / 2 - 1; i >= 0; i--) heapify(arr, n, i);
    for (int i = n - 1; i > 0; i--) {
        int t = arr[0]; arr[0] = arr[i]; arr[i] = t;
        heapify(arr, i, 0);
    }
}

static void heapify(int[] arr, int n, int root) {
    int largest = root, l = 2 * root + 1, r = 2 * root + 2;
    if (l < n && arr[l] > arr[largest]) largest = l;
    if (r < n && arr[r] > arr[largest]) largest = r;
    if (largest != root) {
        int t = arr[root]; arr[root] = arr[largest]; arr[largest] = t;
        heapify(arr, n, largest);
    }
}

// For a live priority queue:
// PriorityQueue<Integer> pq = new PriorityQueue<>();
// pq.offer(item);   // O(log n)
// pq.poll();        // O(log n)`,
    },
  ],

  sampleDataset: {
    label: 'Omnitech push notification urgency scores — 2M queued, arriving continuously',
    preview: [92, 45, 78, 31, 88, 61, 97, 24, 83, 56, 99, 40],
    atScale: {
      n: 2000000,
      leftLabel: 'Re-sort on each insert',
      leftNote: 'O(n) per insert',
      rightLabel: 'Priority Queue (Heap)',
      rightNote: 'O(log n) insert + pop',
    },
  },
}
